import { computed, Injectable, signal } from "@angular/core";
import { ScheduleLotCard } from "../models/ScheduleLotCard";
import { LotPrimaryIdentifier } from "../../../../../core/feature-flags/models/AdminConfigDto";
import {
  collectAvailableValues,
  countSelected,
  FilterChip,
  FilterDefinition,
  FilterSelection,
  filterItems,
  removeSelectedValue,
  toFilterChips
} from "../../../../../shared/components/filter-panel/filter-panel.model";

export const BQ_STEP_FILTER_ID = "bq-step";
export const COMMUNITY_FILTER_ID = "community";
export const SORT_SECTION_ID = "sort";

export enum ScheduleSortDirection {
  Ascending = "asc",
  Descending = "desc"
}

export type ScheduleFilterDefinition = FilterDefinition<ScheduleLotCard>;

export type ScheduleFilterChip = FilterChip;

export interface ScheduleSortOption {
  direction: ScheduleSortDirection;
  label: string;
  /** Condensed form for the section row's pill. */
  shortLabel: string;
}

/**
 * Filtering and ordering for the CM schedule, kept out of the page so the panel and the list
 * read the same state. The predicate itself is the shared one, so a section behaves exactly as it
 * does on Open Items: OR within a section, AND across sections.
 */
@Injectable({
  providedIn: "root"
})
export class ScheduleFilterService {
  private readonly _selected = signal<FilterSelection>({});
  readonly selected = this._selected.asReadonly();

  private readonly _sortDirection = signal<ScheduleSortDirection>(ScheduleSortDirection.Ascending);
  readonly sortDirection = this._sortDirection.asReadonly();

  readonly definitions: ScheduleFilterDefinition[] = [
    {
      id: BQ_STEP_FILTER_ID,
      label: "BQ Step",
      getValue: card => card.steps.map(step => step.getStepLabel()),
      availableValues: [],
      getLabel: value => value
    },
    {
      id: COMMUNITY_FILTER_ID,
      label: "Financial Community",
      getValue: card => card.financialCommunityName,
      availableValues: [],
      getLabel: value => value
    }
  ];

  readonly selectedCount = computed(() => countSelected(this.definitions, this._selected()));

  readonly activeChips = computed<ScheduleFilterChip[]>(() =>
    toFilterChips(this.definitions, this._selected()));

  /**
   * Restricts every section to the values actually on this CM's schedule, so no option can be
   * offered that would return nothing.
   */
  initializeAvailableValues(cards: ScheduleLotCard[]): void {
    collectAvailableValues(cards, this.definitions);
  }

  filterCards(cards: ScheduleLotCard[], selected: FilterSelection): ScheduleLotCard[] {
    return filterItems(cards, this.definitions, selected);
  }

  applyFilters(cards: ScheduleLotCard[]): ScheduleLotCard[] {
    return this.filterCards(cards, this._selected());
  }

  /**
   * Ordered by whichever identifier the division shows on the card, with the scheduled day as the
   * tiebreak so two lots sharing an identifier stay in date order.
   */
  sortCards(
    cards: ScheduleLotCard[],
    identifier: LotPrimaryIdentifier,
    direction: ScheduleSortDirection
  ): ScheduleLotCard[] {
    const keyOf = (card: ScheduleLotCard) =>
      `${(identifier === LotPrimaryIdentifier.Address ? card.streetAddress : card.lotBlockText) ?? ""}`;
    const factor = direction === ScheduleSortDirection.Descending ? -1 : 1;

    return [...cards].sort((left, right) => {
      const diff = keyOf(left).localeCompare(keyOf(right), undefined, { numeric: true, sensitivity: "base" });

      return diff !== 0
        ? factor * diff
        : left.effectiveDate.getTime() - right.effectiveDate.getTime();
    });
  }

  getSortOptions(identifier: LotPrimaryIdentifier): ScheduleSortOption[] {
    return identifier === LotPrimaryIdentifier.Address
      ? [
        { direction: ScheduleSortDirection.Ascending, label: "A to Z", shortLabel: "Address: A to Z" },
        { direction: ScheduleSortDirection.Descending, label: "Z to A", shortLabel: "Address: Z to A" }
      ]
      : [
        { direction: ScheduleSortDirection.Ascending, label: "Low to High", shortLabel: "Lot #: Low to High" },
        { direction: ScheduleSortDirection.Descending, label: "High to Low", shortLabel: "Lot #: High to Low" }
      ];
  }

  getSortOptionLabel(identifier: LotPrimaryIdentifier, direction: string): string {
    return this.getSortOptions(identifier).find(option => option.direction === direction)?.label ?? "";
  }

  getSortOptionShortLabel(identifier: LotPrimaryIdentifier, direction: string): string {
    return this.getSortOptions(identifier).find(option => option.direction === direction)?.shortLabel ?? "";
  }

  getSortLabel(identifier: LotPrimaryIdentifier): string {
    return identifier === LotPrimaryIdentifier.Address ? "Address" : "Lot #";
  }

  /** The active sort as a chip, so it can sit in the same row as the applied filters. */
  getSortChip(identifier: LotPrimaryIdentifier): ScheduleFilterChip {
    const direction = this._sortDirection();

    return {
      filterId: SORT_SECTION_ID,
      filterLabel: "Sort Order",
      value: direction,
      displayLabel: this.getSortOptionShortLabel(identifier, direction)
    };
  }

  applyDraft(draft: FilterSelection, sortDirection: ScheduleSortDirection): void {
    this._selected.set({ ...draft });
    this._sortDirection.set(sortDirection);
  }

  removeChip(filterId: string, value: string): void {
    this._selected.update(current => removeSelectedValue(current, filterId, value));
  }

  clear(): void {
    this._selected.set({});
  }
}
